/**
 * D5 — confirmed one-click access going from on to off, for the same account.
 *
 * The arm control hides when access is gone, and before this the whole
 * toolbar cell went with it: the button, the size box and the status line
 * vanished together, and the trader's next press was silent. Now the host
 * keeps its cell, showing only the status line, until the trader dismisses
 * it, and one-click turns off with the revocation sentence as a MANUAL
 * disarm (D4: losing access is a decision about an S hold, not a board
 * change, so the hold goes and Attack is not brought back on a later arm).
 *
 * Only a confirmed profile that now says "off" counts. A grant that is merely
 * unconfirmed (a restored session, a failed profile read) keeps its slot as
 * `pending` and is not a revocation; a different account or org is an
 * identity change, which the host's own context disarm names.
 *
 * The transition is derived during render (React's "state from the previous
 * render" pattern), so the commit that loses access already holds the notice
 * and the cell never disappears for a frame.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOneClickStore } from '@/stores/oneClickStore';

interface Seen {
  access: boolean;
  identity: string;
  revoked: boolean;
  /** `frame` as of the last render before the notice went up. */
  frame: boolean;
}

export interface OneClickRevocation {
  /** The notice is up: the host keeps its cell and shows the status line. */
  revoked: boolean;
  /** While the notice is up: the host's readout frame was showing when
   *  access went, so the host holds its place (nothing reflows). */
  holdFrame: boolean;
  dismiss: () => void;
}

export function useOneClickRevocation({
  access,
  accessKnownOff,
  identity,
  reason,
  frame = false,
}: {
  /** The confirmed capability the host's controls need. */
  access: boolean;
  /** The confirmed profile says the capability is off (its grant slot is
   *  `hidden`), as opposed to not yet confirmed. */
  accessKnownOff: boolean;
  /** Who the access belongs to (user and org). */
  identity: string;
  /** What the status line says. */
  reason: string;
  /** The host's readout frame is showing (it goes with the controls, often
   *  in the same render that loses access, so the last value before the
   *  notice is the one held). */
  frame?: boolean;
}): OneClickRevocation {
  const [seen, setSeen] = useState<Seen>({ access, identity, revoked: false, frame });
  let current = seen;
  if (seen.access !== access || seen.identity !== identity) {
    const sameIdentity = seen.identity === identity;
    const revokedNow = sameIdentity && !access && ((seen.access && accessKnownOff) || seen.revoked);
    current = { access, identity, revoked: revokedNow, frame: revokedNow ? seen.frame : frame };
  } else if (!seen.revoked && seen.frame !== frame) {
    current = { ...seen, frame };
  }
  if (current !== seen) setSeen(current);
  const { revoked } = current;
  const holdFrame = revoked && current.frame;

  const disarm = useOneClickStore((s) => s.disarm);
  // The sentence this notice put on the strip, so it can take it back.
  const said = useRef<string | null>(null);
  useEffect(() => {
    if (revoked) {
      // An already-off strip keeps its disarm mark: a result that landed
      // after the real 1-Click off ("2 NOT cancelled") stays beside the
      // revocation sentence instead of being hidden by it (D5).
      const { armed, disarmSeq } = useOneClickStore.getState();
      disarm(reason, { manual: true });
      if (!armed) useOneClickStore.setState({ disarmSeq });
      said.current = reason;
      return;
    }
    // Dismissed, or access came back: the sentence goes with the notice, so
    // a later cell never shows "turned off for your account" beside an
    // enabled 1-Click.
    const sentence = said.current;
    said.current = null;
    if (sentence == null) return;
    const store = useOneClickStore.getState();
    if (!store.armed && store.disarmReason === sentence) useOneClickStore.setState({ disarmReason: null });
    // The reason is read when access is lost; a later copy change does not
    // re-disarm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revoked, disarm]);

  const dismiss = useCallback(() => {
    setSeen((s) => (s.revoked ? { ...s, revoked: false } : s));
  }, []);

  return { revoked, holdFrame, dismiss };
}
